import {
  useDeleteLegalClientBeneficiary,
  useDeleteLegalClientBranch,
  useDeleteLegalClientHead,
  useDeleteLegalClientLegalHead,
} from 'api/requests';
import { LegalClientEntity, PermissionType } from 'api/types/entity';
import { useServerErrorNotify } from 'hooks';
import { useTranslation } from 'libs/i18n';
import { useNotify } from 'libs/notify';
import { usePermissions } from 'libs/permissions';

export const useDeleteParticipants = (
  legalClient: LegalClientEntity | null | undefined,
  refetchLegalClient: () => void
) => {
  const notify = useNotify();
  const { t } = useTranslation();
  const { checkPermissions } = usePermissions();

  const { deleteLegalClientBeneficiary, error: deleteBeneficiaryError } =
    useDeleteLegalClientBeneficiary();

  const { deleteLegalClientHead, error: deleteHeadError } =
    useDeleteLegalClientHead();

  const { deleteLegalClientLegalHead, error: deleteLegalHeadError } =
    useDeleteLegalClientLegalHead();

  const { deleteLegalClientBranch, error: deleteBranchError } =
    useDeleteLegalClientBranch();

  const handleSuccessDelete = (message: string) => {
    notify.info(message);
    refetchLegalClient();
  };

  const onDeleteBeneficiary = checkPermissions([
    PermissionType.LegalClientBeneficiary_DeleteOne,
  ])
    ? async (id: string, reason: string) => {
        if (
          await deleteLegalClientBeneficiary({
            id,
            reason,
            versionNumber: legalClient?.versionNumber!,
          })
        ) {
          handleSuccessDelete(
            t('client.legal.legalClientBeneficiary.successDeleteMessage')
          );
        }
      }
    : undefined;

  const onDeleteHead = checkPermissions([
    PermissionType.LegalClientHead_DeleteOne,
  ])
    ? async (id: string, reason: string) => {
        if (
          await deleteLegalClientHead({
            id,
            reason,
            versionNumber: legalClient?.versionNumber!,
          })
        ) {
          handleSuccessDelete(
            t('client.legal.legalClientHead.successDeleteMessage')
          );
        }
      }
    : undefined;

  const onDeleteLegalHead = checkPermissions([
    PermissionType.LegalClientHead_DeleteOne,
  ])
    ? async (id: string, reason: string) => {
        if (
          await deleteLegalClientLegalHead({
            id,
            reason,
            versionNumber: legalClient?.versionNumber!,
          })
        ) {
          handleSuccessDelete(
            t('client.legal.legalClientHead.successDeleteMessage')
          );
        }
      }
    : undefined;

  const onDeleteBranch = checkPermissions([
    PermissionType.LegalClientBranch_DeleteOne,
  ])
    ? async (id: string, reason: string) => {
        if (
          await deleteLegalClientBranch({
            id,
            reason,
            versionNumber: legalClient?.versionNumber!,
          })
        ) {
          handleSuccessDelete(
            t('client.legal.legalClientBranch.successDeleteMessage')
          );
        }
      }
    : undefined;

  useServerErrorNotify(
    deleteBeneficiaryError ??
      deleteHeadError ??
      deleteLegalHeadError ??
      deleteBranchError
  );

  return {
    onDeleteBeneficiary,
    onDeleteHead,
    onDeleteLegalHead,
    onDeleteBranch,
  };
};
